import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  api,
  can,
  readIf,
  shortDate,
  signersText,
  tierLabel,
  type ActivationGate,
  type ActivationView,
  type GateConditionState,
  type GateStage,
  type RiskException,
} from '../lib/api'
import {
  PINNED_SENTENCE,
  RAISE_WORDS,
  kindLabel,
  raiseOffers,
  waiverLine,
  type RaiseKind,
  type RaiseOffer,
} from '../lib/acceptance'
import { vendorLifecycle } from '../lib/lifecycle'
import { useSave, type Save } from '../lib/save'
import { BandChip } from './BandChip'
import { DataTable, ErrorBox, Loading, type Column } from './DataTable'
import { useKept } from './exit/hooks'
import { Dialog, ProgressRing, Status, Toggletip, type Tone } from './kit'
import { RefusalNote } from './RefusalNote'
import { SignatureStages } from './SignatureStages'
import { RaiseRequestForm } from './vendor/RaiseRequest'
import { ReasonTip } from './vendor/ReasonTip'
import '../styles/sweep.css'

// What has to be true before this vendor can be activated, and who has yet to
// sign.
//
// Until this existed the approve button was live or greyed for one reason,
// whoever last moved the vendor. The gate is the rest of the answer, and it is
// a checklist rather than a verdict: a person told no wants to know which of
// the six things is missing and what to do about each, and a person told
// nothing at all presses the button to find out.
//
// Every sentence here is the API's. The condition names, the words shown when
// one is unmet, the reason each is asked, the detail of what was found on this
// vendor today and the roles that can sign a stage all arrive in the response.
// Nothing on this screen is composed from a second copy of the rules, so the
// refusal on the button and the checklist beside it cannot drift apart.
//
// # ONE LINE PER CONDITION
//
// Each condition shows its state and its name. What was found today is behind
// a hover beside the state, and with what would meet it and why it is asked,
// which run to several sentences each, in a drawer from the condition's name
// rather than under every row. The conditions this tier is not held to are named together on one line,
// because each of them said the same sentence.

function whereWords(gate: ActivationGate): string {
  return gate.tierLabel || tierLabel(gate.tier) || 'this tier'
}

const CONDITION_WORD: Record<string, { text: string; tone: Tone }> = {
  met: { text: 'Met', tone: 'plain' },
  outstanding: { text: 'Outstanding', tone: 'warn' },
  'not required': { text: 'Not required', tone: 'plain' },
}

function conditionWord(state: string): { text: string; tone: Tone } {
  return CONDITION_WORD[state] ?? { text: state, tone: 'neutral' }
}

// The conditions in force, summary first (release 3.3, lane 2 leftovers): the
// ones still outstanding in one table, the ones met named on one line, the
// ones this tier is not held to on another. Each name opens the same drawer
// (found today, what would meet it, why it is asked). As a card each, eight
// conditions were 400 px and the Approval part a window and two thirds.
export function GateChecklist({ gate }: { gate: ActivationGate }) {
  const [opened, setOpened] = useState<GateConditionState | null>(null)
  const shown = useKept(opened)
  const inForce = gate.conditions.filter((c) => c.state !== 'not required')
  const offTier = gate.conditions.filter((c) => c.state === 'not required')
  const metList = inForce.filter((c) => c.state === 'met')
  const outstanding = inForce.filter((c) => c.state !== 'met')
  const met = metList.length
  const columns: Column<GateConditionState>[] = [
    {
      key: 'name',
      header: 'Condition',
      render: (c) => (
        <span className="vd-inline">
          <button type="button" className="linkish" onClick={() => setOpened(c)}>
            {c.name}
          </button>
          <ReasonTip hover label={'Found today for ' + c.name.toLowerCase()} text={c.detail} />
        </span>
      ),
    },
    {
      key: 'state',
      header: 'State',
      render: (c) => {
        const word = conditionWord(c.state)
        return <Status tone={word.tone}>{word.text}</Status>
      },
    },
  ]

  return (
    <>
      <p className="summary-line sw-row">
        <ProgressRing done={met} total={inForce.length} label={'Conditions met at ' + whereWords(gate)} />
        {gate.unmet.length === 0 ? (
          <Status tone="ok">All met</Status>
        ) : (
          <Status tone="warn">{gate.unmet.length} outstanding</Status>
        )}
        <span>
          {inForce.length} {inForce.length === 1 ? 'condition' : 'conditions'} in force at {whereWords(gate)}
        </span>
      </p>

      {outstanding.length > 0 && (
        <div className="gate-table">
          <DataTable
            caption={'Conditions outstanding at ' + whereWords(gate)}
            columns={columns}
            rows={outstanding}
            rowKey={(c) => c.code}
            firstRows={10}
            empty={{ title: 'Nothing is outstanding.', detail: '' }}
          />
        </div>
      )}

      {metList.length > 0 && (
        <p className="act-detail sw-row">
          <span>Met:</span>
          {metList.map((c) => (
            <button key={c.code} type="button" className="linkish" onClick={() => setOpened(c)}>
              {c.name}
            </button>
          ))}
        </p>
      )}

      {/* A shorter gate should read as a decision somebody made rather than as
          a gap, so the conditions this tier is not held to are still named. */}
      {offTier.length > 0 && (
        <p className="act-detail muted sw-row">
          <span>Not required at {whereWords(gate)}:</span>
          {offTier.map((c) => (
            <button key={c.code} type="button" className="linkish" onClick={() => setOpened(c)}>
              {c.name}
            </button>
          ))}
        </p>
      )}

      <Dialog shape="drawer" kind="record" open={opened !== null} onClose={() => setOpened(null)} title={shown?.name ?? ''}>
        {shown && (
          <div className="sw-stack">
            <p className="sw-row">
              <Status tone={conditionWord(shown.state).tone}>{conditionWord(shown.state).text}</Status>
            </p>
            {shown.detail && (
              <div>
                <h3>Found today</h3>
                <p>{shown.detail}</p>
              </div>
            )}
            {shown.state === 'outstanding' && (
              <div>
                <h3>What would meet it</h3>
                <p>{shown.unmet}</p>
              </div>
            )}
            <div>
              <h3>Why this is asked</h3>
              <p>{shown.why}</p>
            </div>
          </div>
        )}
      </Dialog>
    </>
  )
}

// Why this reader may not sign a stage of this vendor's approval, or '' when
// they may.
//
// The stage in front of the vendor is decided by the API (release 3.2):
// nextStageRefusal is the signing route's own rule applied to this reader (the
// stage's office, nobody signing a request they raised or sent, the business
// stage only from the vendor's own business owner, one stage per person), in
// one sentence, or empty when they may sign.
//
// Where the API sent no answer (an API from before it, or the moment after a
// signature before the read comes back), three walls, tested in the order the
// API tests them. First the capability, which is a property of the roles on
// the token. Then maker-checker on the move: whoever last put this vendor up
// for approval asked for the decision, and asking is not deciding, so the API
// refuses them at every stage of the round. Then maker-checker on the chain:
// one person may sign one stage of a round and no more. No role lifts either of
// the last two.
export function activationRefusal(view: ActivationView, stage: GateStage, subject: string, lastActor: string): string {
  if (stage.next && view.nextStageRefusal !== undefined) return view.nextStageRefusal
  const stages = view.activation.stages
  const alreadySigned = stages.find((s) => s.signed && subject !== '' && s.signedBy === subject)
  const movedItLast = subject !== '' && lastActor !== '' && lastActor === subject
  const called = stage.name || 'This stage'
  if (!can(stage.capability)) {
    const who = signersText(stage.heldBy)
    return called + ' is for ' + (who || 'nobody, because no role holds it') + '. Your sign-in does not hold it.'
  }
  // The business stage is the vendor's own business owner's, and is not
  // subject to the "last moved it" comparison: the API decides it from the
  // record and sends its sentence for this reader.
  if (stage.capability === 'vendor.business_signoff') {
    if (view.businessSignoffRefusal) return view.businessSignoffRefusal
  } else if (movedItLast) {
    return (
      'You last moved this vendor, so this signature is for somebody else to give. The person who asks for the ' +
      'decision does not make it, at any stage of this round.'
    )
  }
  if (alreadySigned) {
    return (
      'You already signed for this vendor in this round, at ' +
      (alreadySigned.name || 'another stage') +
      (alreadySigned.signedAt ? ', on ' + shortDate(alreadySigned.signedAt) : '') +
      '. Each stage needs a different person, so this one is for somebody else to sign.'
    )
  }
  return ''
}

// What stands in for a missing paper, and which rules the round runs under
// (release 3.2). A standing contract waiver is said in one line until the
// contract is filed, whatever the vendor's status, because a waiver nobody can
// see is a contract gap nobody knows about. Every word here is the API's or a
// fixed sentence; nothing is a code.
export function ApprovalStanding({ gate }: { gate: ActivationGate }) {
  const waiver = gate.contractWaiver
  if (!waiver && !gate.pinnedToEarlierRule) return null
  return (
    <div className="sw-stack" style={{ marginBottom: 12 }}>
      {waiver && (
        <p className="note warn" data-testid="contract-waiver" style={{ margin: 0 }}>
          {waiverLine(waiver)}
        </p>
      )}
      {gate.pinnedToEarlierRule && (
        <p className="note" data-testid="pinned-rule" style={{ margin: 0 }}>
          {PINNED_SENTENCE}
        </p>
      )}
    </div>
  )
}

// What the reader may raise to clear an outstanding condition: the written
// risk acceptance, a contract waiver. Offered only where it can count (see
// lib/acceptance.ts raiseOffers); one already waiting is said rather than
// offered twice, with the way to its sign-offs in the register.
export function RaiseOffers({ offers, onRaise }: { offers: RaiseOffer[]; onRaise: (kind: RaiseKind) => void }) {
  if (offers.length === 0) return null
  return (
    <ul className="raise-offers" aria-label="What you can raise to clear it" data-testid="raise-offers">
      {offers.map((o) => (
        <li key={o.kind}>
          {o.waiting ? (
            <p className="sw-row" style={{ margin: 0 }}>
              <Status tone="info">Waiting for sign-off</Status>
              <span>
                {kindLabel(o.waiting)} raised{o.waiting.expiresOn ? ', to run until ' + shortDate(o.waiting.expiresOn) : ''}
              </span>
              <a href={'#/exceptions?open=' + encodeURIComponent(o.waiting.id)}>Open it</a>
            </p>
          ) : (
            <p className="sw-row" style={{ margin: 0 }}>
              <button type="button" onClick={() => onRaise(o.kind)}>
                {o.label}
              </button>
              <ReasonTip label={'Who approves a ' + RAISE_WORDS[o.kind].heading.toLowerCase()} text={RAISE_WORDS[o.kind].who} />
            </p>
          )}
        </li>
      ))}
    </ul>
  )
}

// The form behind the two requests, in its dialog. It holds no state, so a
// test can read what the form is handed. The form is keyed by the kind: the
// dialog keeps drawing the kind it was opened for while it slides away, and
// the other kind opened in that time must start with its own empty fields and
// its own longest term, not with what was typed for the first.
export function RaiseDialog({
  raising,
  shownRaise,
  vendorId,
  vendorName,
  band,
  subject,
  save,
  onClose,
  onRaised,
}: {
  raising: RaiseKind | null
  // The kind last opened, kept while the dialog leaves.
  shownRaise: RaiseKind | null
  vendorId: string
  vendorName: string
  band: string
  subject: string
  save: Save
  onClose: () => void
  onRaised: (note: string) => void
}) {
  return (
    <Dialog shape="modal" kind="form" open={raising !== null} onClose={onClose} title={shownRaise ? RAISE_WORDS[shownRaise].heading : ''}>
      {shownRaise && (
        <RaiseRequestForm
          key={shownRaise}
          kind={shownRaise}
          vendorId={vendorId}
          vendorName={vendorName}
          band={band}
          subject={subject}
          save={save}
          onCancel={onClose}
          onRaised={onRaised}
        />
      )}
    </Dialog>
  )
}

export function VendorActivation({
  vendorId,
  vendorName = '',
  onSigned,
}: {
  vendorId: string
  vendorName?: string
  onSigned?: () => void
}) {
  const [view, setView] = useState<ActivationView | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [subject, setSubject] = useState('')
  const [busy, setBusy] = useState(false)
  const [signError, setSignError] = useState<ApiError | null>(null)
  const [said, setSaid] = useState('')
  const [lastActor, setLastActor] = useState('')
  // This vendor's rows of the register, so a risk acceptance or a waiver
  // already waiting is not offered again. Read only for a reader who may raise
  // one; null until read.
  const [rows, setRows] = useState<RiskException[] | null>(null)
  const [raising, setRaising] = useState<RaiseKind | null>(null)
  const shownRaise = useKept(raising)
  // One record per vendor and kind, so the answer to one request is never
  // dealt with in the form of the other (lib/save.ts).
  const raiseSave = useSave(raising ? vendorId + ' ' + raising : null, shownRaise ? RAISE_WORDS[shownRaise].heading : 'the request')
  const [raised, setRaised] = useState('')
  const mayRaise = can('risk.accept')

  const load = useCallback(() => {
    api
      .vendorActivation(vendorId)
      .then((v) => {
        setView(v)
        setError(null)
      })
      .catch(setError)
    if (mayRaise) {
      readIf('assessment.read', () => api.exceptions(vendorId).then((r) => r.exceptions), [] as RiskException[])
        .then(setRows)
        // Without the rows the offer still stands: a second request is a
        // nuisance for its approvers, not a gap.
        .catch(() => setRows([]))
    }
  }, [vendorId, mayRaise])

  useEffect(load, [load])

  // Who is signed in, because the matrix is satisfied by different people and
  // not by different roles. Somebody who has already signed this round is
  // greyed out with the stage they signed rather than told after the fact.
  useEffect(() => {
    let live = true
    api
      .me()
      .then((m) => {
        if (live) setSubject(m.subject)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [])

  // Who last moved this vendor. The third wall on the button is neither the
  // capability nor a signature already given: whoever asked for the decision
  // may not make it, and the API refuses them with SELF_APPROVAL after the
  // capability check. The name lives on the lifecycle read, so it is read
  // rather than guessed at from the record.
  useEffect(() => {
    let live = true
    vendorLifecycle(vendorId)
      .then((l) => {
        if (live) setLastActor(l.lastActor)
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [vendorId])

  function sign(stageIndex: number, note: string) {
    setBusy(true)
    setSignError(null)
    api
      .signApprovalStage(vendorId, { stageIndex, note: note.trim() })
      .then((r) => {
        setSaid(r.note)
        // The reply carries the evaluation as it stands after the signature, so
        // what is drawn is what the write produced rather than a second read of
        // a moving target. The API's answer on the next stage was about the
        // stage just signed, so it is dropped until the read below brings the
        // answer for the stage now in front; until then the rules below it
        // (one stage per person) keep the button shut for the signer.
        setView((prev) => (prev ? { ...prev, activation: r.activation, nextStageRefusal: undefined } : prev))
        load()
        onSigned?.()
      })
      .catch((err) => setSignError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setBusy(false))
  }

  if (error != null) {
    return (
      <section className="panel">
        <h2>Activation</h2>
        <ErrorBox error={error} what="The activation gate" />
      </section>
    )
  }
  if (view == null) return <Loading what="the activation gate" />

  const gate = view.activation
  const refusal = (stage: GateStage) => activationRefusal(view, stage, subject, lastActor)

  return (
    <>
      <section className="panel panel-flush" aria-labelledby="activation-heading">
        <div className="panel-head">
          <div>
            <h2 id="activation-heading" style={{ margin: 0 }}>
              Activation
            </h2>
            <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
              Round {gate.round} against approval matrix v{gate.matrixVersionNo}
              {can('config.read') && (
                <>
                  {', '}
                  <a href="#/approval-matrix">read the matrix</a>
                </>
              )}
            </p>
          </div>
          <Status tone={gate.met ? 'ok' : 'warn'}>{gate.met ? 'Ready to activate' : 'Not ready'}</Status>
        </div>

        <div className="act-body">
          {/* A vendor already in the register is evaluated and never demoted,
              and the note is the API saying which of the two situations this
              is. It is what stops this panel reading as a threat to a live
              supplier, so its point stays on the panel, on the line that says
              which band and tier the gate is for, and its words are one press
              away. */}
          <p className="summary-line sw-row" style={{ marginTop: 0 }}>
            <span>
              For band <BandChip band={gate.band} /> at <strong>{whereWords(gate)}</strong>
            </span>
            <Toggletip label="If the band or tier moves">
              The conditions and the chain below are the ones for this band and tier. Move either and the matrix may ask
              for different signatures.
            </Toggletip>
            {view.note && (
              <span className="sw-row" data-testid="not-enforced">
                <Status tone="info">Not enforced now</Status>
                <span>Shown as it would stand if approved today</span>
                <ReasonTip label="Why the gate is not enforced now" text={view.note} />
              </span>
            )}
          </p>
          <ApprovalStanding gate={gate} />
          <GateChecklist gate={gate} />
          {raised && (
            <div className="ok-note" role="status">
              {raised}
            </div>
          )}
          {/* Not until this vendor's rows are read, so a request already
              waiting is never offered a second time while they load. */}
          <RaiseOffers
            offers={raiseOffers(gate, { canRaise: mayRaise && rows !== null, status: view.status, rows })}
            onRaise={(kind) => {
              setRaised('')
              setRaising(kind)
            }}
          />
        </div>
      </section>

      <RaiseDialog
        raising={raising}
        shownRaise={shownRaise}
        vendorId={vendorId}
        vendorName={vendorName}
        band={gate.band}
        subject={subject}
        save={raiseSave}
        onClose={() => setRaising(null)}
        onRaised={(note) => {
          setRaising(null)
          // The API's own sentence: who approves it, and that nobody
          // approves their own request.
          setRaised(note)
          load()
        }}
      />

      <section className="panel" aria-labelledby="signatures-heading">
        <h2 id="signatures-heading" className="sw-title">
          Signatures
          <Toggletip label="How the signatures work">
            The stages are signed in order, each by a different person, and a stage opens once the ones above it are
            done.
          </Toggletip>
        </h2>

        {said && (
          <div className="ok-note" role="status">
            {said}
          </div>
        )}
        {/* A refusal is the API's sentence and nothing else; a failure keeps
            its code and reference for support. */}
        {signError && <RefusalNote error={signError} heading="Not signed" what="The signature" verb="recorded" />}

        {gate.stages.length === 0 ? (
          <div className="empty">
            <strong>The matrix asks for no signatures here</strong>
            Draft and promote a matrix version before approving anything{' '}
            <Toggletip label="Why there are no signatures">
              That is a matrix this product refuses to promote, so the stored version is older than the rules that check
              it.
            </Toggletip>
          </div>
        ) : (
          <SignatureStages
            stages={gate.stages}
            actionable={(s) => s.next && view.gateApplies}
            offered={(s) =>
              s.next && !view.gateApplies ? (
                <span className="muted sw-row">
                  Nothing here can be signed now
                  <Toggletip label="Why this stage cannot be signed">
                    The gate is enforced on the move from pending approval into the register, and this vendor is not
                    making that move.
                  </Toggletip>
                </span>
              ) : undefined
            }
            refusal={refusal}
            noteLabel="Note, optional, kept with the signature"
            busy={busy}
            onSign={sign}
            actionId="tprm.exit-workspace,vendor,vendor-record.a01"
          />
        )}
      </section>
    </>
  )
}
